import * as THREE from 'three';

import { updateCameraConfig } from '../../state/appConfig';
import type { Vector3Like } from '../sceneConfig';
import { CameraDirector } from './CameraDirector';

interface PreviewTourParams {
    cameraDirector: CameraDirector;
    clock: THREE.Clock;
    fov: number;
    startPosition: Vector3Like;
    endPosition?: Vector3Like;
    endTarget?: Vector3Like;
    /** Duration of the "enter" transition (initial pose → startPosition). Default: 1. */
    enterDuration?: number;
    /** Duration of the "exit" transition (orbit → endPosition). Default: 1. */
    exitDuration?: number;
}

const DEFAULT_TRANSITION_DURATION = 1;

export class PreviewTour {
    private clock: THREE.Clock;
    private cameraDirector: CameraDirector;
    private fov: number;
    private _completed = false;
    private orbitCenter = new THREE.Vector3(0, 0, 0);
    private startPosition: THREE.Vector3;
    private endPosition: THREE.Vector3;
    private endTarget: THREE.Vector3 | null;
    private enterDuration: number;
    private exitDuration: number;
    private deferred: { promise: Promise<void>, resolve: () => void };

    constructor(params: PreviewTourParams) {
        this.clock = params.clock;
        this.cameraDirector = params.cameraDirector;
        this.fov = params.fov;
        this.startPosition = new THREE.Vector3(
            params.startPosition.x, params.startPosition.y, params.startPosition.z
        );
        this.endPosition = params.endPosition
            ? new THREE.Vector3(params.endPosition.x, params.endPosition.y, params.endPosition.z)
            : this.startPosition.clone();
        this.endTarget = params.endTarget
            ? new THREE.Vector3(params.endTarget.x, params.endTarget.y, params.endTarget.z)
            : null;
        this.enterDuration = params.enterDuration ?? DEFAULT_TRANSITION_DURATION;
        this.exitDuration = params.exitDuration ?? DEFAULT_TRANSITION_DURATION;
        let resolve = () => {};
        const promise = new Promise<void>(r => {
            resolve = r;
        });
        this.deferred = { promise, resolve };
    }

    get completed() {
        return this._completed;
    }

    setOrbitCenter(orbitCenter: THREE.Vector3): void {
        this.orbitCenter.copy(orbitCenter);
    }

    start(): void {
        this.cameraDirector.transitionToPose({
            position: this.startPosition,
            target: this.orbitCenter,
            fov: this.fov,
        }, this.enterDuration, () => {
            updateCameraConfig({ orbitAutoMove: true });
        }, this.clock.getElapsedTime(), false);
    }

    /** Mark tour as completed immediately without running any camera transition. */
    complete(): void {
        if (this._completed) return;
        updateCameraConfig({ orbitAutoMove: false });
        this._completed = true;
        this.deferred.resolve();
    }

    stop(overrideEnd?: { position: THREE.Vector3; target: THREE.Vector3 }): Promise<void> {
        // If the tour was already completed synchronously (e.g. via
        // `complete()` from a dev-time force-finish), this `stop()` would
        // otherwise launch a redundant `transitionToPose` that fights the
        // user's free-fly input. Bail out and return a pre-resolved promise
        // so normal-path `await previewTour.stop()` continues immediately.
        if (this._completed) return Promise.resolve();
        // CRITICAL: disable orbitAutoMove BEFORE transitionToPose, otherwise
        // SceneControls.update() keeps rotating the orbit camera each frame in
        // parallel with the transition — final pose ends up offset by the
        // accumulated rotation over the transition duration.
        updateCameraConfig({ orbitAutoMove: false });
        const endPos = overrideEnd?.position ?? this.endPosition;
        const endTgt = overrideEnd?.target ?? this.endTarget ?? this.orbitCenter;
        this.cameraDirector.transitionToPose({
            position: endPos,
            target: endTgt,
            fov: this.fov,
        }, this.exitDuration, () => {
            this._completed = true;
            this.deferred.resolve();
        }, this.clock.getElapsedTime(), false);
        return this.deferred.promise;
    }
}
